import React, { useEffect, useMemo, useRef, useState } from "react";
import {motion} from "framer-motion";
import {
	Card, CardHeader, CardBody, CardTitle, Row, Col, Table
} from "reactstrap";
import RefStrap from "lib/Utils/RefStrap";
import Brand from "../lib/Core/Backend/Controllers/Brand";
import CardType from "../lib/Core/Backend/Controllers/CardType";
import Supplier from "../lib/Core/Backend/Controllers/Supplier";
import PurchaseOrder from "../lib/Core/Backend/Controllers/PurchaseOrder";
import DataTable from "components/Core/DataTable";
import { SwipeableDrawer } from "@mui/material";
import OrderDetails from "components/PurchaseOrder/OrderDetails";
import useCatalogs from "hooks/useCatalogs";

const MotionCard = motion(RefStrap(Card));
const cardControl = {
	animate: {
		transition: {
			delayChildren: 0.05, staggerChildren: 0.1, staggerDirection: 1
		}
	}
}
const fadeIn = {
	initial: {opacity: 0, scale: 0.6}, animate: {opacity: 1, scale: 1,transition: {duration: 0.2, ease: "easeOut"}},
};

const dollar = Intl.NumberFormat('en-US', {
	style: "currency",
	currency: "USD",
});

function SupplierDashboard(props)
{
	const Notify = props.notify;
	const supId = props.match.params.id;
	const {token} = props;
	const {currencies} = useCatalogs();

	const supCtl = useMemo(() => new Supplier(token),[token]);
	const poCtl = useMemo(() => new PurchaseOrder(token), [token]);
	const brandsCtl = useMemo(() => new Brand(token), [token]);
	const cardCtl = useMemo(() => new CardType(token), [token]);

	const [openDrawer, setOpenDrawer] = useState(false);
	const [orderDetail, setOrderDetail] = useState({});

	const toggleDrawer = () => setOpenDrawer(!openDrawer)

	const [brands, setBrands] = useState([]);
	const [cardTypes, setCardTypes] = useState([]);
	const [supplier, setSupplier] = useState(null);
	const [balance, setBalance] = useState();

	const table = useRef();

	const header = [
		{title: 'Date', data: 'createdAt', sortable: true, searchable: true},
		{title: 'Name', data: 'name', sortable: true, searchable: true},
		{title: 'Total Amount', data: 'totalAmount', sortable: true, searchable: true, render: data => dollar.format(data)},
		{title: 'Total Quantity', data: 'totalQuantity', sortable: true, searchable: true},
		{title: 'Status', data: 'status', sortable: true, searchable: true, render: (data) => {
				if(data){
					switch (data) {
						case '1':
							return `<span style="width: 100px" class="badge badge-pill text-white bg-wisteria">open</span>`;
						
						case '2':
							return `<span style="width: 100px" class="badge badge-pill text-white bg-nileBlue">in progress</span>`;

						case '3':
							return `<span style="width: 100px" class="badge badge-pill text-white bg-radicalRed">canceled</span>`;

						case '4': 
							return `<span style="width: 100px" class="badge badge-pill text-white bg-shamrock">completed</span>`;
						case '5':
							return `<span style="width: 100px" class="badge badge-pill text-white bg-warning">On hold</span>`;
						default: return 'Error'
					}
				}
			},
			renderForSelect: data => {
				if(data){
					switch (data) {
						case '1':
							return {
								label: `open`,
								value: data
							}
						
						case '2':
							return {
								label: `in progress`,
								value: data
							}

						case '3':
							return {
								label: `canceled`,
								value: data
							}

						case '4': 
							return {
								label: `completed`,
								value: data
							}

						default: return {
							label: 'error',
							value: -1
						}
					}
				}
			}
		}
	];

	const prepareDetails = (row) => {
		setOrderDetail(row.data());
		toggleDrawer();
	}

	useEffect(() => {
		brandsCtl.all(data => {
			if(data.error || data.errorMessage){
				Notify(alert, 'danger', data.errorMessage ?? data.error)
			} else {
				setBrands(data);
			}
		})

		cardCtl.all(data => {
			if(data.error || data.errorMessage){
				Notify(alert, 'danger', data.errorMessage ?? data.error)
			} else {
				setCardTypes(data);
			}
		})

	},[brandsCtl, Notify, cardCtl])

	useEffect(() => {
		supCtl.byId(supId, data => {
			if(data.errorMessage)
			{
				Notify("danger", "Supplier doesn't exist.");
				window.location.href = "/admin/supply";
			}
			else
			{
				props.setTitle(data.name);
				setSupplier(data)
			}
		});

		poCtl.allByPurchaserSourceBySourceTypeIdAll(supId, 1,data => {
			if(data.errorMessage || data.error) {
				Notify("danger", data.errorMessage ?? data.error);
			} else {
				if(table.current){
					let purchaseOrders = data.map(po => {
						let currency = currencies.find(currency => currency.id = po.currencyId)?.code ?? 'USD'
						return {...po, status: `${po.status}`, currency}
					})
					table.current.dataTable.clear().rows.add(purchaseOrders).draw();
				}
			}
		});
	}, [])

	useEffect(() => {
		supCtl.thresholdAmount(supId, data => {
			if(!(data.error || data.errorMessage)){
				setBalance(dollar.format(data.thresholdAmount))
			}
		})
	}, [supCtl, supId]);

	return (<>
		<motion.div initial="initial" animate="animate" exit="exit" variants={cardControl} className="content">
			<Row>
				<Col lg="4" md="6" sm="6">
					<MotionCard variants={fadeIn} className="card-stats">
						<CardBody>
							<Row>
								<Col md="4" xs="5">
									<div className="icon-big text-center icon-warning">
										<i className="nc-icon nc-money-coins text-success"/>
									</div>
								</Col>
								<Col md="8" xs="7">
									<div className="numbers">
										<p className="card-category">Current balance</p>
										<CardTitle tag="p">{balance}</CardTitle>
										<p/>
									</div>
								</Col>
							</Row>
						</CardBody>
					</MotionCard>
				</Col>
				<Col lg="3" md="6" sm="6">
					<MotionCard variants={fadeIn} className="card-stats">
						<CardBody>
							<Row>
								<Col md="4" xs="5">
									<div className="icon-big text-center icon-warning">
										<i className="nc-icon nc-vector text-danger"/>
									</div>
								</Col>
								<Col md="8" xs="7">
									<div className="numbers">
										<p className="card-category">Current status:</p>
										<CardTitle tag="p" className="text-success">Active</CardTitle>
										<p/>
									</div>
								</Col>
							</Row>
						</CardBody>
					</MotionCard>
				</Col>
				<Col lg="12" md="12" sm="6">
					<MotionCard variants={fadeIn} className="card-stats">
						<CardBody>
							<Row>
								<Col md="1" xs="5" className="text-left">
									<div className="icon-big text-center icon-warning text-left">
										<i className="nc-icon nc-delivery-fast text-primary"/>
									</div>
								</Col>
								<Col md="11" xs="7">
									<div className="numbers">
										<p className="card-category">Brand List</p>
										<CardTitle tag="p">Avaliable brands</CardTitle>
										<p/>
									</div>
									<Table width="80%">
										<thead>
											<td>Brand</td>
											<td>Discount</td>
											<td>Fee</td>
										</thead>
										<tbody>
											{supplier && 
												supplier.supplierBrandList.map((brand, id) => {
													return (
														<tr key={`brand-${id}`}>
															<td>{brand?.brand?.name}</td>
															<td>{brand?.purchaseDiscount}</td>
															<td>{brand?.purchaseFee}</td>
														</tr>
													)
												})
										}
										</tbody>
										
									</Table>
								</Col>
							</Row>
						</CardBody>
					</MotionCard>
				</Col>
			</Row>
			<Row>
				<Col lg="12" md="12" sm="12">
					<MotionCard variants={fadeIn} className="card-stats">
						<CardHeader>
							<Row>
								<Col md="4" xs="5">
											<div className="icon-big text-center icon-warning">
												<i className="nc-icon nc-globe text-warning"/>
											</div>
										</Col>
										<Col md="8" xs="7">
											<div className="numbers mt-3">
												<p className="card-category">Purchase orders</p>
											</div>
										</Col>
									</Row>
						</CardHeader>
						<CardBody>
							<Row className="my-4 justify-content-center">
								<Col sm="11">
									<DataTable ref={table} columns={header} 
										data={null}
										options={{
											searching: true,
											saveState: false,
											order: [[0, 'desc']],
											dom: 'tip'
										}}
										addCustomButtons={[
											{
												callback: prepareDetails,
												name: 'purchaserDetails',
												icon: "fas fa-info-circle",
												style: 'padding: 5px 10px; background: transparent; font-size: 1.2rem',
												props: 'data-toggle="tooltip" data-placement="top" title="See Order details"'
										 },
										]}
										enableSelectFilters={true}
									/>
								
								</Col>
							</Row>
						</CardBody>
					</MotionCard>
				</Col>
			</Row>
			<SwipeableDrawer
				anchor="right"
				open={openDrawer}
				onClose={toggleDrawer}
        onOpen={toggleDrawer}
			>
				<div style={{width: '60vw'}}>
					<OrderDetails order={orderDetail} token={token} brands={brands.result} cardTypes={cardTypes}  openDrawer={openDrawer}/>
				</div>
			</SwipeableDrawer>
		</motion.div>
	</>);
}

export default SupplierDashboard;
